import React from 'react';
import { IconHamburger, IconClose, IconShareChatHeader } from './ConciergeIcons';
import { ConciergeHomeLogoWidget, ConciergeHomeMobileNavWidget } from '@brandclub/bc-widgets';
import ConciergeBagButton from './ConciergeBagButton';

interface ConciergeHomeMobileNavProps {
  brandLogoUrl?: string;
  onMenuClick?: () => void;
  onCloseClick?: () => void;
  /**
   * When provided, renders a share button alongside the close (X) button so
   * the user can both share the current conversation and still exit the
   * Concierge.
   */
  onShareClick?: () => void;
  /**
   * Renders the bag button left of Share. The mobile nav owns the top-right
   * corner, so a floating bag there covers the close button. Needs a
   * ConciergeContextProvider with a commerce adapter above it.
   */
  showBag?: boolean;
  /**
   * Accessible name for the close button. The button does different things
   * depending on where the user is (leave the Concierge vs. close the current
   * conversation), and a bare "Close" gave no clue which.
   */
  closeLabel?: string;
}

export const ConciergeHomeMobileNav: React.FC<ConciergeHomeMobileNavProps> = ({
  brandLogoUrl,
  onMenuClick,
  onCloseClick,
  onShareClick,
  showBag = false,
  closeLabel = 'Close',
}) => {
  return (
    <ConciergeHomeMobileNavWidget>
      {(state, icons, resolveColor) => {
        const bgColor = resolveColor(state.navBgColor);
        const hamburgerColor = resolveColor(state.hamburgerIconColor);
        const closeColor = resolveColor(state.closeIconColor);

        // Keep the logo visually centered by giving the left side the same
        // width as the right action group. Right group = close icon, plus
        // one (icon + 12px gap) for each of bag and share that shows.
        const ACTION_GAP = 12;
        const actionCount = 1 + (onShareClick ? 1 : 0) + (showBag ? 1 : 0);
        const rightGroupWidth = state.closeIconSize * actionCount + ACTION_GAP * (actionCount - 1);

        return (
          <nav
            data-concierge-mobile-nav
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              height: state.navHeight,
              padding: `${state.navPaddingY}px ${state.navPaddingX}px`,
              boxSizing: 'border-box',
              flexShrink: 0,
              color: hamburgerColor || undefined,
              ...(bgColor && bgColor !== 'transparent' ? { backgroundColor: bgColor } : {}),
            }}
          >
            {/* Hamburger menu — wrapped so its slot matches the right
                action group's width and the logo stays centered. */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                width: rightGroupWidth,
                flexShrink: 0,
              }}
            >
              <button
                type="button"
                onClick={onMenuClick}
                aria-label="Open menu"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: state.hamburgerIconSize,
                  height: state.hamburgerIconSize,
                  padding: 0,
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                }}
              >
                {icons.hamburgerIconUrl ? (
                  <img
                    src={icons.hamburgerIconUrl}
                    alt="Menu"
                    style={{
                      width: state.hamburgerIconSize,
                      height: state.hamburgerIconSize,
                      objectFit: 'contain',
                    }}
                  />
                ) : (
                  <IconHamburger size={state.hamburgerIconSize} style={{ color: hamburgerColor }} />
                )}
              </button>
            </div>

            {/* Center: Brand logo (separate widget, editable independently) */}
            {state.showLogo ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: 1,
                  minWidth: 0,
                  padding: '0 8px',
                }}
              >
                <div style={{ width: '100%', maxWidth: 120 }}>
                  <ConciergeHomeLogoWidget
                    widgetId="concierge-home-mobile-nav-logo"
                    displayName="Mobile Nav Logo"
                    initialState={{
                      logoWidth: 79,
                      mobile_logoWidth: 79,
                      logoMaxHeight: 18,
                    }}
                    logoUrl={brandLogoUrl}
                  />
                </div>
              </div>
            ) : (
              <div style={{ width: state.closeIconSize, height: state.closeIconSize }} />
            )}

            {/* Right actions: Bag + Share (when in a conversation) + Close.
                Share used to replace the close button, but that left users
                without a way to exit while chatting — render both so the
                close affordance is always present. */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: ACTION_GAP,
                width: rightGroupWidth,
                flexShrink: 0,
              }}
            >
              {showBag && <ConciergeBagButton size={state.closeIconSize} color={closeColor} />}
              {onShareClick && (
                <button
                  type="button"
                  onClick={onShareClick}
                  aria-label="Share conversation"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: state.closeIconSize,
                    height: state.closeIconSize,
                    padding: 0,
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                  }}
                >
                  <IconShareChatHeader size={24} style={{ color: closeColor }} />
                </button>
              )}
              <button
                type="button"
                onClick={onCloseClick}
                aria-label={closeLabel}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: state.closeIconSize,
                  height: state.closeIconSize,
                  padding: 0,
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                }}
              >
                {icons.closeIconUrl ? (
                  <img
                    src={icons.closeIconUrl}
                    alt="Close"
                    style={{
                      width: state.closeIconSize,
                      height: state.closeIconSize,
                      objectFit: 'contain',
                    }}
                  />
                ) : (
                  <IconClose size={state.closeIconSize} style={{ color: closeColor }} />
                )}
              </button>
            </div>
          </nav>
        );
      }}
    </ConciergeHomeMobileNavWidget>
  );
};
